'use client';

import React, { useState, useEffect, useRef } from 'react';

interface Review {
  name: string;
  location: string;
  rating: number;
  text: string;
  date: string;
  initials: string;
  color: string;
}

const reviews: Review[] = [
  {
    name: 'Maria G.',
    location: 'Rio Rancho, NM',
    rating: 5,
    text: 'Absolutely amazing results! My driveway looks brand new after years of oil stains. The team was professional, on time, and thorough. Will definitely book again!',
    date: 'June 2026',
    initials: 'MG',
    color: 'bg-teal',
  },
  {
    name: 'James T.',
    location: 'Albuquerque, NM',
    rating: 5,
    text: 'Had the house wash and window cleaning done. The difference is night and day. My home looks like it was just painted. Highly recommend Rio Grande Property Solutions!',
    date: 'May 2026',
    initials: 'JT',
    color: 'bg-slate-700',
  },
  {
    name: 'Sandra L.',
    location: 'Corrales, NM',
    rating: 5,
    text: 'They cleaned my solar panels and I immediately noticed an improvement in energy output. Very knowledgeable team, careful with the equipment. 5 stars!',
    date: 'May 2026',
    initials: 'SL',
    color: 'bg-teal-dark',
  },
  {
    name: 'Robert M.',
    location: 'Bernalillo, NM',
    rating: 5,
    text: 'Best pressure washing company in New Mexico. Fair pricing, excellent communication, and the results speak for themselves. My roof looks incredible.',
    date: 'April 2026',
    initials: 'RM',
    color: 'bg-slate-600',
  },
  {
    name: 'Patricia H.',
    location: 'Placitas, NM',
    rating: 5,
    text: 'I got the Full Property Refresh package and it was worth every penny. House, driveway, gutters, windows — everything is spotless. Incredible value!',
    date: 'April 2026',
    initials: 'PH',
    color: 'bg-teal',
  },
  {
    name: 'Carlos V.',
    location: 'Los Ranchos, NM',
    rating: 5,
    text: 'Professional, efficient, and the results are stunning. My commercial property has never looked better. Customers have actually commented on how clean it looks!',
    date: 'March 2026',
    initials: 'CV',
    color: 'bg-slate-700',
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill={i < rating ? '#F59E0B' : '#E5E7EB'}>
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
        </svg>
      ))}
    </div>
  );
}

export default function RGPSReviews() {
  const [current, setCurrent] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startAuto = () => {
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % reviews.length);
    }, 4000);
  };

  useEffect(() => {
    startAuto();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  const goTo = (i: number) => {
    setCurrent(i);
    if (intervalRef.current) clearInterval(intervalRef.current);
    startAuto();
  };

  return (
    <section id="reviews" className="bg-white py-20 px-4 sm:px-6">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-teal-light text-teal font-bold text-sm px-4 py-2 rounded-full mb-4">
            ⭐ Customer Reviews
          </div>
          <h2 className="text-dark-text font-bold mb-4" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            What Our Customers Say
          </h2>
          <div className="flex items-center justify-center gap-2">
            <div className="flex gap-0.5">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} width="20" height="20" viewBox="0 0 24 24" fill="#F59E0B">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              ))}
            </div>
            <span className="text-dark-text font-bold">5.0</span>
            <span className="text-gray-text">· 50+ Google Reviews</span>
          </div>
        </div>

        {/* Featured Review */}
        <div className="bg-light-gray rounded-3xl p-8 sm:p-10 mb-8 shadow-card">
          <div className="flex items-start gap-5">
            <div className={`${reviews[current].color} w-14 h-14 rounded-2xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
              {reviews[current].initials}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-3 mb-2">
                <span className="font-bold text-dark-text text-lg">{reviews[current].name}</span>
                <span className="text-gray-text text-sm">{reviews[current].location}</span>
                <span className="text-gray-text text-sm ml-auto">{reviews[current].date}</span>
              </div>
              <StarRating rating={reviews[current].rating} />
              <p className="text-gray-text text-base leading-relaxed mt-3 italic">
                "{reviews[current].text}"
              </p>
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`rounded-full transition-all ${
                i === current ? 'w-8 h-2.5 bg-teal' : 'w-2.5 h-2.5 bg-gray-200 hover:bg-teal/40'
              }`}
            />
          ))}
        </div>

        {/* Mini review cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {reviews.slice(0, 3).map((review, i) => (
            <button
              key={review.name}
              onClick={() => goTo(i)}
              className={`text-left p-5 rounded-2xl border-2 transition-all ${
                i === current ? 'border-teal bg-teal-light' : 'border-gray-100 bg-white hover:border-teal/30'
              }`}
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`${review.color} w-9 h-9 rounded-xl flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                  {review.initials}
                </div>
                <div>
                  <div className="font-bold text-dark-text text-sm">{review.name}</div>
                  <StarRating rating={review.rating} />
                </div>
              </div>
              <p className="text-gray-text text-xs leading-relaxed line-clamp-2">"{review.text}"</p>
            </button>
          ))}
        </div>

        {/* Google Review CTA Card */}
        <div className="mt-12">
          <div
            className="bg-white rounded-3xl shadow-card border border-gray-100 px-8 py-10 sm:px-12 sm:py-12 text-center mx-auto max-w-2xl"
            style={{ borderTop: '4px solid #0D9488' }}
          >
            {/* Teal icon */}
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 rounded-2xl bg-teal-light flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="#0D9488"/>
                </svg>
              </div>
            </div>

            {/* Headline */}
            <h3
              className="text-dark-text font-bold mb-3"
              style={{ fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontFamily: "'Plus Jakarta Sans', sans-serif" }}
            >
              Loved Your Service?
            </h3>

            {/* 5-star row */}
            <div className="flex justify-center gap-1 mb-5">
              {Array.from({ length: 5 }).map((_, i) => (
                <svg key={i} width="28" height="28" viewBox="0 0 24 24" fill="#F59E0B">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              ))}
            </div>

            {/* Subheadline */}
            <p className="text-gray-text text-base leading-relaxed mb-8 max-w-lg mx-auto">
              If Rio Grande Property Solutions helped make your property look better, we'd truly appreciate your feedback. Your review helps our small local business grow and helps other customers feel confident choosing us.
            </p>

            {/* CTA Button */}
            {/* Replace the href below with the official Google Review link from Google Business Profile */}
            <a
              href="PASTE_GOOGLE_REVIEW_LINK_HERE"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-teal text-white font-bold text-base sm:text-lg px-8 py-4 rounded-2xl shadow-md hover:bg-teal-dark transition-all duration-200 w-full sm:w-auto"
              style={{ minWidth: '260px' }}
            >
              ⭐ Leave Us a Google Review
            </a>

            {/* Thank you line */}
            <p className="text-gray-text text-sm mt-5">
              Thank you for supporting a local New Mexico business.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
